// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  IconButton,
  DialogContent,
  Dialog,
  DialogTitle,
  Typography,
  Grid,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import {
  ReactMuiTableV8,
  ReactMuiTableColumnHeaderTextEllipsis,
} from '@nuva-org/solytics-frontend';
import PropTypes from 'prop-types';

import useModalDisclosure from '../../../../hooks/useModalDisclosure';
import { useDialogFullscreen } from '../../../../hooks/useDialogFullscreen';
import SearchTextField from '../../../../components/Common/SearchTextField';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import AssociationFieldTableViewComponentSkeleton from '../../../Skeleton/AssociationFieldTableViewComponentSkeleton';
import {
  createDynamicColumns,
  getTableColumns,
  isValueEmpty,
  getEntityTypeKey,
  getLabelPathFromLabelConfig,
  getEntityTypes,
} from '../../../../utils/Utils';
import { DEFAULT_ROUTE_CONFIG } from '../../../../const/defaultRoutesConfig';
import { ENTITY_TYPE as ENTITY_TYPE_MAP } from '../../../../const/CommonConst';
import {
  TABLE_CONTAINER_BOX_SX,
  TOOLBAR_ROW_SX,
  TOOLBAR_SEARCH_SX,
} from '../../../../const/tableToolbarStyles';
import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import {
  useLinkedEntitiesAllAttributesQuery,
  useDownstreamLinkedEntitiesTableQuery,
  useUpstreamEntitiesQuery,
} from '../../../../hooks/ModelInventory/useAsscoiationTableFieldQueries';
import TableTextCell from '../../Tables/TableTextCell';

const ENTITY_TYPE = 'ModelAssociation'; //will be replaced with entitytypeid after core sync

const SEARCH_DEBOUNCE_MS = 300;

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 5;

const renderLinkedEntityCell = ({
  dataArray,
  isRedirectionEnabled = false,
}) => {
  return (
    <TableTextCell
      entries={dataArray}
      isRedirectionEnabled={isRedirectionEnabled}
    />
  );
};
renderLinkedEntityCell.propTypes = {
  dataArray: PropTypes.arrayOf(PropTypes.string).isRequired,
  isRedirectionEnabled: PropTypes.bool,
};

const constructUrl = ({ entityId, entityType, requestType }) => {
  const entityTypeKey = getEntityTypeKey(entityType, requestType);
  const dynamicEntityPath = getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
  );
  const pathname = `/${dynamicEntityPath}/${entityId}`;
  const search =
    entityTypeKey === 'ModelAssociation' ? '?tab=0' : '?tab=summary';

  const type = ENTITY_TYPE_MAP[getEntityTypes(entityType, requestType)];

  return { pathname, search, dynamicEntityPath, type };
};

const parseDerivedFormulaDirection = (derivedFormula) => {
  const direction = derivedFormula.includes('"direction": "upstream"')
    ? 'upstream'
    : 'downstream';
  return direction;
};

const getUpstreamOptionLabel = (option) => option?.label || '';

const isUpstreamOptionEqualToValue = (option, value) =>
  option?.entity_id === value?.entity_id;

const hardCodedColumns = [
  {
    id: 'linked_entity_id',
    heading: 'Linked Entity ID',
    accessor: 'linked_entity',
    Cell: ({ row }) => {
      const linkedEntityDetails = row?.original?.linked_entity || [];
      const linkedEntityId =
        linkedEntityDetails?.map((entity) => ({
          label: entity?.id,
          value:
            constructUrl({
              entityId: entity?.id,
              entityType: entity?.entity_type,
              requestType: entity?.request_type,
            })?.pathname || '',
        })) || [];

      return renderLinkedEntityCell({
        dataArray: linkedEntityId,
        isRedirectionEnabled: true,
      });
    },
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 200,
  },
  {
    id: 'linked_entity_name',
    heading: 'Linked Entity Name',
    accessor: 'linked_entity',
    Cell: ({ row }) => {
      const linkedEntityDetails = row?.original?.linked_entity || [];
      const linkedEntityNames =
        linkedEntityDetails?.map((entity) => entity?.entity_name) || [];

      return renderLinkedEntityCell({
        dataArray: linkedEntityNames,
      });
    },
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 200,
  },
];

// ─── Component Definition ──────────────────────────────────────────────────────────

const AssociationFieldTableViewComponent = ({
  attribute = {},
  entityId = null,
  entityType = null,
}) => {
  const {
    open: openPreview,
    handleOpen: openModal,
    handleClose: closeModal,
  } = useModalDisclosure();
  const { isFullScreen, toggleFullScreen, exitFullScreen } =
    useDialogFullscreen();

  const [selectedUpstreamEntity, setSelectedUpstreamEntity] = useState(null);

  const handleClosePreview = useCallback(() => {
    closeModal();
    setSelectedUpstreamEntity(null);
  }, [closeModal]);

  const handleOpenPreview = useCallback(() => {
    openModal();
  }, [openModal]);

  const { activeTemplateType, attributeId, direction } = useMemo(() => {
    const template = attribute?.association_template || {};
    const templateType = entityType || template?.template_type || ENTITY_TYPE;
    const id = attribute?.id || '';
    const derivedFormulaDirection = parseDerivedFormulaDirection(
      attribute?.derived_formula
    );

    return {
      activeTemplateType: templateType,
      attributeId: id,
      direction: derivedFormulaDirection,
    };
  }, [
    attribute?.association_template,
    attribute?.id,
    attribute?.derived_formula,
    entityType,
  ]);

  const { pluginConfig } = useSelector((state) => state?.tenant);
  const isFCBEnabled = useMemo(() => !!pluginConfig?.FCB, [pluginConfig]);

  const isUpstream = direction === 'upstream';

  // ─── Queries ─────────────────────────────────────────────────────────────────────

  const { data: upstreamEntitiesData, isLoading: isUpstreamLoading } =
    useUpstreamEntitiesQuery(
      {
        entityId,
        entityType: activeTemplateType,
        attributeId,
      },
      {
        enabled: isUpstream && Boolean(attributeId) && Boolean(openPreview),
      }
    );

  const upstreamEntities = useMemo(() => {
    const rawEntities = upstreamEntitiesData?.upstream_entities || [];
    return rawEntities
      .filter((item) => {
        if (isFCBEnabled) {
          return (
            item?.entity_type === 'ModelInventory' &&
            item?.request_type === 'Normal'
          );
        }
        return true;
      })
      .map((item) => {
        const alias = item?.entity_alias || '';
        const uid = item?.unique_id || '';
        const label = alias && uid ? `${alias} (${uid})` : alias || uid || '';
        return {
          ...item,
          label,
        };
      });
  }, [upstreamEntitiesData, isFCBEnabled]);

  useEffect(() => {
    if (openPreview && isUpstream && upstreamEntities.length === 1) {
      setSelectedUpstreamEntity(upstreamEntities[0]);
    }
  }, [openPreview, isUpstream, upstreamEntities]);

  const selectedUpstreamEntityId = selectedUpstreamEntity?.entity_id || null;

  const isQueryEnabled = isUpstream
    ? Boolean(entityId) &&
      Boolean(attributeId) &&
      Boolean(openPreview) &&
      Boolean(selectedUpstreamEntityId)
    : Boolean(entityId) && Boolean(attributeId) && Boolean(openPreview);

  const { data: columnsHeaderData } = useLinkedEntitiesAllAttributesQuery(
    {
      entityId,
      entityType: activeTemplateType,
      attributeId,
    },
    {
      enabled: isQueryEnabled,
    }
  );

  const { data: tableData, isLoading: isTableLoading } =
    useDownstreamLinkedEntitiesTableQuery(
      {
        entityId,
        entityType: activeTemplateType,
        page: INITIAL_PAGE,
        pageSize: INITIAL_SIZE,
        attributeId,
        ...(isUpstream && selectedUpstreamEntityId
          ? { parentEntityId: selectedUpstreamEntityId }
          : {}),
      },
      { enabled: isQueryEnabled }
    );

  // ─── Search state ────────────────────────────────────────────────────────────────

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const handleSearchChange = useCallback(
    (e) => {
      setSearchTerm(e?.target?.value || '');
    },
    [setSearchTerm]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Raw rows as returned by the API, before any client-side filtering.
  const rawTableData = useMemo(() => {
    if (Array.isArray(tableData)) return tableData;
    return [];
  }, [tableData]);

  // Frontend-only search filtering — no refetching, just narrowing what's already fetched.
  const filteredTableData = useMemo(() => {
    if (!debouncedSearchTerm) return rawTableData;
    return rawTableData.filter((row) =>
      JSON.stringify(row)
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase())
    );
  }, [rawTableData, debouncedSearchTerm]);

  const columns = useMemo(() => {
    if (isValueEmpty(columnsHeaderData)) return [];

    const dynamicColumns = createDynamicColumns({
      data: getTableColumns(columnsHeaderData),
      width: 200,
      HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
      entityType: activeTemplateType,
    });

    return [...hardCodedColumns, ...dynamicColumns];
  }, [columnsHeaderData, activeTemplateType]);

  const handleGetRowProps = useCallback(
    (row) => ({
      onDoubleClick: () => {
        const targetId = row?.original?.unique_id;

        if (!targetId) return;

        const targetEntityType = row?.original?.entity_type;
        const targetRequestType = row?.original?.request_type;

        const { pathname, search } = constructUrl({
          entityId: targetId,
          entityType: targetEntityType,
          requestType: targetRequestType,
        });

        if (pathname) {
          window.open(`${pathname}${search || ''}`, '_blank');
        }
      },
      style: {
        cursor: 'pointer',
      },
    }),
    [activeTemplateType]
  );

  // ─── Upstream Selector Rendering ─────────────────────────────────────────────────

  const handleUpstreamEntityChange = useCallback((_event, newValue) => {
    setSelectedUpstreamEntity(newValue);
  }, []);

  const renderUpstreamInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Select Upstream Entity"
        placeholder="Choose an entity to view its linked records"
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
      />
    ),
    []
  );

  const renderUpstreamSelector = useCallback(() => {
    if (!isUpstream) return null;

    if (isUpstreamLoading) return null;

    if (upstreamEntities.length === 1) return null;

    return (
      <Box mb={2}>
        <Autocomplete
          options={upstreamEntities}
          value={selectedUpstreamEntity}
          onChange={handleUpstreamEntityChange}
          getOptionLabel={getUpstreamOptionLabel}
          isOptionEqualToValue={isUpstreamOptionEqualToValue}
          size="small"
          disableClearable={!selectedUpstreamEntity}
          popupIcon={<DropdownArrow />}
          renderInput={renderUpstreamInput}
          disabled={upstreamEntities.length === 1}
        />
      </Box>
    );
  }, [
    isUpstream,
    isUpstreamLoading,
    upstreamEntities,
    selectedUpstreamEntity,
    handleUpstreamEntityChange,
    renderUpstreamInput,
  ]);

  // ─── Table Content Rendering ─────────────────────────────────────────────────────

  const renderTableContent = useCallback(() => {
    if (isUpstream && isUpstreamLoading) {
      return <AssociationFieldTableViewComponentSkeleton />;
    }

    if (isUpstream && !isUpstreamLoading && upstreamEntities.length === 0) {
      return null;
    }

    if (
      isUpstream &&
      upstreamEntities.length > 1 &&
      !selectedUpstreamEntityId
    ) {
      return (
        <Box
          display="flex"
          sx={{ height: '400px' }}
          flexGrow={1}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <NoDataPresentComponent message="Select an upstream entity above to view its linked records." />
        </Box>
      );
    }

    if (isTableLoading) {
      return <AssociationFieldTableViewComponentSkeleton />;
    }

    if (isValueEmpty(rawTableData)) {
      const emptyMessage = isUpstream
        ? 'No linked records found for the selected entity.'
        : `No ${attribute?.label || 'records'} found.`;

      return (
        <Box
          display="flex"
          sx={{ height: '400px' }}
          flexGrow={1}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <NoDataPresentComponent message={emptyMessage} />
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            width: '300px',
            ...TOOLBAR_ROW_SX,
            pl: 0,
            pr: 0,
          }}
        >
          <SearchTextField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search"
            sx={TOOLBAR_SEARCH_SX}
          />
        </Box>
        <Box sx={TABLE_CONTAINER_BOX_SX}>
          <ReactMuiTableV8
            data={filteredTableData}
            columns={columns}
            enablePagination
            initialPageSize={INITIAL_SIZE}
            rowsPerPageOptions={[5, 10, 15]}
            showOptions
            stickyHeader
            getRowProps={handleGetRowProps}
          />
        </Box>
      </>
    );
  }, [
    isUpstream,
    isUpstreamLoading,
    upstreamEntities.length,
    selectedUpstreamEntityId,
    isTableLoading,
    rawTableData,
    filteredTableData,
    columns,
    searchTerm,
    handleSearchChange,
    handleGetRowProps,
    attribute?.label,
  ]);

  return (
    <Grid container xs={12}>
      <Grid item xs={12}>
        <Typography
          variant="body2"
          component="span"
          onClick={handleOpenPreview}
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          View {attribute?.label || 'Entity Association Table'}
        </Typography>
      </Grid>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth={isFullScreen ? false : 'lg'}
        fullWidth
        fullScreen={isFullScreen}
        TransitionProps={{
          onExited: () => exitFullScreen(),
        }}
        PaperProps={{
          sx: {
            maxHeight: isFullScreen ? '100vh' : '90vh',
            height: isFullScreen ? '100vh' : 'auto',
          },
        }}
      >
        <DialogTitle>
          <Box component="span" aria-live="polite" aria-atomic="true">
            {attribute?.label}
          </Box>
        </DialogTitle>

        {/* Fullscreen Button */}
        <Tooltip title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}>
          <IconButton
            color="primary"
            aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
            onClick={toggleFullScreen}
            sx={{
              position: 'absolute',
              right: 56,
              top: 8,
            }}
          >
            {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <IconButton
          color="primary"
          aria-label="Close dialog"
          onClick={handleClosePreview}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          {renderUpstreamSelector()}
          {renderTableContent()}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

AssociationFieldTableViewComponent.propTypes = {
  attribute: PropTypes.shape({}).isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
};

export default AssociationFieldTableViewComponent;
